import React from "react"
import PropTypes from "prop-types"
import {PaymentCurrencyType, WalletEntityType} from "@irati/types"
import apps from "@irati-web/app/src/routeConfig"
import authentication from "@irati/auth"
import {FormattedCurrency, FormattedMessage, injectIntl} from "@irati/utils/lang"

import WalletBallance from "@irati/wallet/apollo/query/Wallet"
import Flex from "@irati/ui/src/Flex"
import {Col} from "@irati/ui/src/ColumnRow"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {Body, H3} from "@irati/ui/src/Typography"
import {ButtonRouterLink} from "@irati/ui/src/Link"

import mainTheme from "@irati/ui/src/global/theme"
import tracking from "@irati-tracking/recognition/navigation"
import WalletPoints from "@irati/ui/src/WalletPoints"
import {InjectIntlProps} from "@irati/utils/lang/injectIntl"
import ManagerRewardsModal from "../ManagerRewardsModal"
import {WalletRoutes} from "../../enums/Routes"
import {StyledCard, Trigger} from "./styled"
import CardContainer from "./CardContainer"

interface Props extends InjectIntlProps {}

const ManagerRewardPointsWidget: React.FC<Props> = () => {
  const canReward = authentication.apps("wallet.flexiRewards.canRewardEmployees")

  if (!canReward) {
    return null
  }

  return (
    <CardContainer to="/flexi-reward" app={apps.WALLET}>
      <StyledCard medium flush marginTop={4}>
        <Trigger>
          <ManagerRewardsModal />
        </Trigger>
        <H3 color={mainTheme.colors.utility.white} marginBottom>
          <FormattedMessage id="platform.reward.managerWidget.title" />
        </H3>
        <Flex direction="column" marginTop={mainTheme.spacing.two} marginBottom={mainTheme.spacing.two} gap={mainTheme.spacing.one}>
          <Body small color={mainTheme.colors.greys.lightest}>
            <FormattedMessage id="platform.reward.managerWidget.balanceDescription" />
          </Body>
          <WalletPoints color={mainTheme.colors.utility.white}>
            <WalletBallance
              fetchPolicy="network-only"
              variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
              {({data}) => <FormattedCurrency value={data.wallet?.amount} currency={PaymentCurrencyType.Rp} />}
            </WalletBallance>
          </WalletPoints>
        </Flex>

        {/* Mobile */}
        <Col smHidden mdHidden lgHidden>
          <ButtonRouterLink inverted tertiary stopPropagation onClick={tracking.openRewardWallet} to="/flexi-reward" app={apps.WALLET}>
            <Flex>
              <FormattedMessage id="platform.reward.managerWidget.secondaryCta" />
              <Icon icon={IconTypes.arrowRight} marginLeft />
            </Flex>
          </ButtonRouterLink>
        </Col>

        {/* Desktop */}
        <Col xsHidden>
          <ButtonRouterLink
            primary
            small
            full
            marginBottom
            stopPropagation
            onClick={tracking.giveRewardsPoints}
            to={WalletRoutes.FLEXI_ASSIGN}
            app={apps.WALLET}>
            <FormattedMessage id="platform.reward.managerWidget.primaryCta" />
          </ButtonRouterLink>
          <ButtonRouterLink
            stopPropagation
            inverted
            full
            small
            onClick={tracking.openRewardWallet}
            to={WalletRoutes.FLEXI_REWARD}
            app={apps.WALLET}>
            <FormattedMessage id="platform.reward.managerWidget.secondaryCta" />
          </ButtonRouterLink>
        </Col>
      </StyledCard>
    </CardContainer>
  )
}

ManagerRewardPointsWidget.contextTypes = {
  apps: PropTypes.object.isRequired
}

export default injectIntl(ManagerRewardPointsWidget)
